import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SelectValue } from 'antd/lib/select'
import { useDispatch, useSelector } from 'react-redux'
import dp from 'dot-prop-immutable'

import { Id } from 'src/types'

import { ApplicationState } from 'src/store'
import {
  addResource,
  getResources
} from 'src/core/store/ducks/resources/actions'
import { PAGE_SIZE_SEARCH } from 'src/core/store/ducks/resources'
import { Option, SelectProps } from 'src/components/Formik/Select'
import { ResourcesAvailable } from 'src/core/store/ducks/resources/types'
import { Select } from 'src/components/Formik'

interface Props<T extends keyof ResourcesAvailable>
  extends Omit<SelectProps, 'loading' | 'options'> {
  allowAll?: boolean
  edit?: Id
  keyLabel: keyof ResourcesAvailable[T]
  keyValue: keyof ResourcesAvailable[T]
  organization_id?: Id
  resource: T
}

function SelectResource<K extends keyof ResourcesAvailable>({
  allowAll = false,
  defaultActiveFirstOption = true,
  formikProps,
  keyLabel,
  keyValue,
  name,
  onChange,
  organization_id,
  resource,
  ...props
}: Props<K>): JSX.Element {
  const { resources } = useSelector((state: ApplicationState) => ({
    resources: state.resources
  }))

  const [searchTerm, setSearchTerm] = useState<string>('')
  const refTimeout = useRef<NodeJS.Timeout>(null)

  const dispatch = useDispatch()

  function optionMap(option: ResourcesAvailable[K]): Option {
    const code =
      (keyLabel !== 'code' && option.code && `${option.code} - `) || ''
    const label = `${code}${option[keyLabel]}`
    return {
      label,
      title: option.name.toString(),
      value: option[keyValue]?.toString()
    }
  }

  function optionFilter(option: any) {
    const code =
      (keyLabel !== 'code' && option.code && `${option.code} - `) || ''
    const label = `${code}${option[keyLabel]}`
    return `${code}${label}${option?.description || ''}`
      .toLowerCase()
      .includes(searchTerm)
  }

  const resourceList = useMemo<Option[]>(
    () =>
      (searchTerm
        ? // @ts-ignore
          resources[resource].list.filter(optionFilter)
        : resources[resource].list
      ).map(optionMap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resources[resource].list, searchTerm]
  )

  useEffect(() => {
    dispatch(getResources(resource, { organization_id, allowAll }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const formikValue = dp.get(formikProps.values, name)
    if (!searchTerm && formikValue) {
      // @ts-ignore
      const exist = resources[resource].list.some(
        (item: any) => `${item[keyValue]}` === `${formikValue}`
      )
      if (!exist) {
        const formikResource = dp.get(formikProps.values, name.slice(0, -3))

        if (formikResource) {
          dispatch(
            addResource(resource, dp.get(formikProps.values, name.slice(0, -3)))
          )
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, keyValue, name, resource, resources[resource].list, searchTerm])

  /**
   * Set standard fields
   */
  useEffect(() => {
    if (!dp.get(formikProps.values, name) && defaultActiveFirstOption) {
      // @ts-ignore
      const standardResource = resources[resource].list.find(
        // @ts-ignore
        ({ standard, standard_type }) =>
          standard && ['REGULAR_SERVICE', undefined].includes(standard_type)
      )

      if (standardResource) {
        setFieldValue(standardResource[keyValue].toString())
        return
      }

      // @ts-ignore
      const defaultValue = resources[resource].list[0]?.[keyValue]?.toString()

      if (defaultValue) {
        setFieldValue(defaultValue)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources[resource].list, defaultActiveFirstOption])

  function setFieldValue(value: string) {
    formikProps.setFieldValue(name, value)

    const resourceObjectName = name.replace('_id', '')
    const splitName = name.split('.')
    const idKey = splitName[splitName.length - 1]

    // @ts-ignore
    const resourceFound = resources[resource].list.find(
      // @ts-ignore
      item => +item[idKey] === +value
    )

    formikProps.setFieldValue(resourceObjectName, resourceFound)
  }

  return (
    <Select
      {...props}
      dataTestId={resource}
      defaultActiveFirstOption={defaultActiveFirstOption}
      filterOption={false}
      formikProps={formikProps}
      loading={resources[resource].loading}
      name={name}
      onChange={(
        value: SelectValue = '',
        option: React.ReactElement<any> | React.ReactElement<any>[]
      ) => {
        setSearchTerm('')
        setFieldValue(`${value}`)
        onChange && onChange(value, option)
      }}
      onSearch={(value = '') => {
        const canDispatch =
          resources[resource].total_results > PAGE_SIZE_SEARCH ||
          resources[resource].total_results !== resources[resource].list.length
        /**
         * @todo this change is provisional
         * There is an error in resource paging when it involves more than one organization
         * could use:
         * resources[resource].total_pages > 1
         */
        if (canDispatch) {
          clearTimeout(refTimeout.current)
          refTimeout.current = setTimeout(() => {
            dispatch(
              getResources(resource, {
                allowAll,
                organization_id,
                search: value
              })
            )
          }, 500)
        }
        setSearchTerm(value.toLowerCase())
      }}
      options={resourceList}
      showSearch
    />
  )
}

export default SelectResource
